import type { ChatReaction } from "../model/types";

// KAN-1329: логика реакций (CHAT-SPEC §3, §6.3). Чистые функции — их зовут пилюли, кружок ❤️,
// двойной клик и аккордеон, а сервер подтверждает снимком.

/** Поставить или снять свою реакцию локально (оптимистично). Пустой эмодзи удаляется из списка. */
export function toggleReactionLocal(reactions: ChatReaction[], emoji: string, me: { id: string; name: string; avatar?: string | null }): ChatReaction[] {
  const cur = reactions.find((r) => r.emoji === emoji);
  const now = new Date().toISOString();
  if (!cur) return [...reactions, { emoji, count: 1, userIds: [me.id], names: [me.name], avatars: [me.avatar ?? null], myAtUtc: now }];
  if (cur.userIds.includes(me.id)) {
    const count = cur.count - 1;
    if (count <= 0) return reactions.filter((r) => r.emoji !== emoji);
    return reactions.map((r) =>
      r.emoji === emoji ? { ...r, count, userIds: r.userIds.filter((id) => id !== me.id), names: r.names.filter((n) => n !== me.name), avatars: r.names.some((n) => n === me.name) ? r.avatars.filter((_, i) => r.names[i] !== me.name) : r.avatars } : r,
    );
  }
  return reactions.map((r) =>
    r.emoji === emoji ? { ...r, count: r.count + 1, userIds: [...r.userIds, me.id], names: [...r.names, me.name].slice(0, 3), avatars: [...r.avatars, me.avatar ?? null].slice(0, 3), myAtUtc: now } : r,
  );
}

/**
 * KAN-1483: снимок реакций из события хаба общий для всех и не знает, когда я ставил свои. Время своих реакций
 * переносим из прежнего снимка — иначе двойной клик после чужой реакции снимал бы не последнюю мою.
 */
export function keepMyReactionTimes(prev: ChatReaction[], next: ChatReaction[], myUserId: string): ChatReaction[] {
  const times = new Map(prev.filter((r) => r.myAtUtc).map((r) => [r.emoji, r.myAtUtc]));
  // Моя реакция, которой не было в прежнем снимке (поставил с другого устройства), — самая свежая.
  const now = new Date().toISOString();
  return next.map((r) => (r.userIds.includes(myUserId) && !r.myAtUtc ? { ...r, myAtUtc: times.get(r.emoji) ?? now } : r));
}

/** Двойной клик: моя последняя по времени реакция (без времени — по порядку снимка); нет своих — undefined. */
export function lastOwnReaction(reactions: ChatReaction[], myUserId: string): string | undefined {
  const mine = reactions.filter((r) => r.userIds.includes(myUserId));
  const timed = mine.filter((r) => r.myAtUtc).sort((a, b) => Date.parse(b.myAtUtc!) - Date.parse(a.myAtUtc!));
  return (timed[0] ?? mine[mine.length - 1])?.emoji;
}

export interface ReactionView extends ChatReaction {
  mine: boolean;
}

/** Сколько разных эмодзи может стоять на сообщении — как на сервере (ChatReactions.MaxDistinct*): в личке 3, в группе 5. */
export function reactionLimit(isGroup: boolean): number {
  return isGroup ? 5 : 3;
}

/** Можно ли поставить эмодзи: уже стоящий (свой или чужой) — всегда, новый — пока не упёрлись в лимит сообщения. */
export function canAddReaction(reactions: ChatReaction[], emoji: string, isGroup: boolean): boolean {
  return reactions.some((r) => r.emoji === emoji) || reactions.length < reactionLimit(isGroup);
}

/**
 * Новая реакция на сообщении, где уже стоит лимит разных эмодзи: заменяет мою последнюю (её снимаем, новую ставим).
 * "add" — ставить как есть; иначе — какую мою снять перед этим; null — заменить нечего (место заняли чужие реакции,
 * или моя последняя стоит не только от меня и места не освободит).
 */
export function reactionPlan(reactions: ChatReaction[], emoji: string, isGroup: boolean, myUserId: string): "add" | { replace: string } | null {
  if (canAddReaction(reactions, emoji, isGroup)) return "add";
  const own = reactions.filter((r) => r.userIds.includes(myUserId) && r.count === 1);
  const last = lastOwnReaction(own, myUserId);
  return last ? { replace: last } : null;
}

/**
 * Что показывать под баббом: по убыванию количества, все «по одной» — в конец (иначе случайные
 * одиночные эмодзи перебивают популярные). Показываются все: лимит держит сервер (3 в личке, 5 в группе),
 * поэтому ни одна реакция не прячется за другими и не «выползает», когда снимают соседнюю.
 */
export function visibleReactions(reactions: ChatReaction[], myUserId: string): ReactionView[] {
  return reactions
    .map((r, i) => ({ r, i }))
    .sort((a, b) => Number(a.r.count === 1) - Number(b.r.count === 1) || b.r.count - a.r.count || a.i - b.i)
    .map(({ r }) => ({ ...r, mine: r.userIds.includes(myUserId) }));
}
